import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  compose,
  withPropsOnChange,
  withStateHandlers,
  branch,
  withHandlers,
} from "recompose";
import { LocalForm, Control, actions, Fieldset, track } from "react-redux-form";
import {
  Col,
  Row,
  Button,
} from "reactstrap";
import { NotificationManager } from "react-notifications";
import {
  FormGroup,
  SaveButton,
  withReload,
} from "components/common";
import Select from "react-select";
import { withXeroErrors } from "components";
import { Trans } from "react-i18next";
import produce from "immer";
import countryList from "react-select-country-list";


const getFieldState = (formState, model) => {
  const keys = model.replace(/^\./, '').split('.');
  return keys.reduce((acc, key) => acc?.[key], formState);
};


const ValidatedField = ({ label, model, formState, children, errorMessage, required }) => {
  const fieldState = getFieldState(formState, model);

  return (
    <div className="mb-2">
      {children}
      {required && <span className="text-info small mr-1">*<Trans>required</Trans></span>}
      {(fieldState?.touched && !fieldState?.valid) ? <span className="text-danger small">({errorMessage})</span> : <span> {" "}</span>}
    </div>
  );

}

const Contact = ({ label, model, t, ...props }) => (
  <Fieldset model={model} className="mb-1" component={FormGroup}>
    <Row noGutters className="my-3">
      <Col xs={12} sm={3} style={{ maxWidth: 100 }}>{label}</Col>
      <Col xs={12} sm={9} className="pr-1">
        <Row noGutters>
          <Col xs="auto" className="pr-1">
            <Control.text
              className="form-control-sm"
              updateOn="blur"
              model=".PhoneCountryCode"
              placeholder={t("+(Code)")}
              style={{ width: 100 }}
            />
          </Col>
          <Col xs="auto">
            <Control.text
              updateOn="blur"
              className="form-control-sm"
              model=".PhoneNumber"
              placeholder={t("Phone Number")}
              style={{ width: 200 }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  </Fieldset>

);

const Address = ({ model, label, t, form, countryOptions, isSaudi, addressIndex}) => {
  const saudiPostal = (v) => (isSaudi ? (v && /^\d{5}$/.test(v)) : true);
  const saudiNumber = (v) => (isSaudi ? (v && /^\d{4}$/.test(v)) : true);
  const saudiRegion = (v) => (isSaudi ? !!v : true)
  const required = (v) => !!v
  
  return (
    <Fieldset model={model} component={FormGroup} label={label}>
      <div className="d-flex flex-column pt-2 pb-2">
        <Control.text
          updateOn="blur"
          placeholder={t("Address Person Name")}
          className="mb-2"
          model=".AttentionTo"
        />
        <Row>
          <Col>
          <ValidatedField errorMessage={t("Building number must be 4 digits")} model={`.Addresses.${addressIndex}.AddressLine4`} formState={form} required={isSaudi}>
            <Control.text
                model=".AddressLine4"
                placeholder={t("Building Number")}
                validators={{ saudiNumber }}
                required={isSaudi}
              />
          </ValidatedField>
          </Col>
          <Col>
            <ValidatedField errorMessage={t("Plot number must be 4 digits")} model={`.Addresses.${addressIndex}.AddressLine3`} formState={form} required={isSaudi}>
              <Control.text
                model=".AddressLine3"
                placeholder={t("Plot Number")}
                validators={{ saudiNumber }}
                required={isSaudi}
              />
            </ValidatedField>
          </Col>
        </Row>
        <ValidatedField errorMessage={t("Address is required")} model={`.Addresses.${addressIndex}.AddressLine1`} formState={form} required={true}>
          <Control.text
            updateOn="blur"
            model=".AddressLine1"
            placeholder={t("Address Line")}
            validators={{ required }}
          />
        </ValidatedField>
        <Row>
          <Col>
          <ValidatedField errorMessage={t("City is required")} model={`.Addresses.${addressIndex}.City`} formState={form} required={true}>
            <Control.text
              updateOn="blur"
              placeholder={t("City/Town")}
              model=".City"
              validators={{ required }}
            />
          </ValidatedField>
          </Col>
          <Col>
          <ValidatedField errorMessage={t("Region is required")} model={`.Addresses.${addressIndex}.Region`} formState={form} required={isSaudi}>
            <Control.text
              updateOn="blur"
              placeholder={t("State/Region")}
              model=".Region"
              validators={{ saudiRegion }}  
            />
            </ValidatedField>
          </Col>
        </Row>
        <Row>
          <Col>
          <ValidatedField errorMessage={t("Postal code must be 5 digits")} model={`.Addresses.${addressIndex}.PostalCode`} formState={form} required={isSaudi}>
            <Control.text
              updateOn="blur"
              placeholder={t("Postal/ Zip Code")}
              model=".PostalCode"
              validators={{ saudiPostal }}
            />
          </ValidatedField>
          </Col>
          <Col>
            <Control
              component={Select}
              model=".Country"
              placeholder={t("Country")}
              options={countryOptions}
              simpleValue
              style={{ backgroundColor: '#F4F7FE' }}
            />
          </Col>
        </Row>
      </div>
    </Fieldset>
  )
};

const ID_TYPES = [
  { id: "CRN", label: "Commercial Register (CR)" },
  { id: "MOM", label: "MOMRA License" },
  { id: "MLS", label: "MLSD License" },
  { id: "SAG", label: "SAGIA License" },
  { id: "NAT", label: "National ID" },
  { id: "IQA", label: "Iqama Number" },
  { id: "PAS", label: "Passport ID" },
  { id: "GCC", label: "GCC ID" },
  { id: "700", label: "700 Number" },
  { id: "OTH", label: "Other ID" }
]

const required_fields = ['ContactInfo']
export const PartialContactForm = ({
  title,
  setState,
  form,
  item,
  onSubmit,
  dispatch,
  errors,
  XeroErrors,
  xero,
  reload,
  t,
  history,
  toggle,
  missFields,
  invoice,
  ...props
}) => {
  const countryOptions = useMemo(() => countryList().getData(), [])

  const idTypes = useMemo(() => ID_TYPES, [])

  const missingMobileNumber = useMemo(() => missFields && (missFields.includes('PhoneNumber')))

  const address = item?.Addresses?.find(address => address.AddressType === 'POBOX') || {};
  const address_index = item?.Addresses?.findIndex(address => address.AddressType === 'POBOX');
  const isSaudi = address?.Country === 'SA'

  const prevIsSaudi = useRef(isSaudi);

  useEffect(() => {
    if (!dispatch) return;
  
    if (prevIsSaudi.current !== isSaudi) {
      prevIsSaudi.current = isSaudi;
      dispatch(actions.setTouched('', true));
    }
  }, [isSaudi]);


  return (
    <LocalForm
      initialState={item}
      onUpdate={(form) => setState({ form })}
      onChange={(item) => setState({ item })}
      onSubmit={onSubmit}
      getDispatch={(dispatch) => setState({ dispatch })}
      validators={{
        'TaxNumber': (v) => (isSaudi ? (v ? /^[3]\d{13}[3]$/.test(v) : true) : true),
      }}
    >
      <XeroErrors />
      {missingMobileNumber &&
        <Contact className="pt-2 pb-2"
          model={track(".Phones[]", { PhoneType: "MOBILE" })}
          label="Mobile"
          t={t}
        />
      }
      {
        required_fields.some(item => missFields.includes(item)) && <>
          <Address className="pt-2 pb-2"
            form={form}
            model={track(".Addresses[]", { AddressType: "POBOX" })}
            addressIndex={address_index}
            label="Tax/Billing Address"
            countryOptions={countryOptions}
            isSaudi={isSaudi}
            t={t}
          />

          <FormGroup label="Tax Number">
          <ValidatedField errorMessage={t("Invalid Tax Number")} model={`.TaxNumber`} formState={form} required={!item.id_number && !item.CompanyNumber}>
            <Control.text
              updateOn="blur"
              model=".TaxNumber"
              placeholder={t("Tax Number")}
              required={!item.id_number && !item.CompanyNumber}
            />
            </ValidatedField>
          </FormGroup>

          <FormGroup label={t("Registration Number")} model=".CompanyNumber" className="pt-2 pb-2">
            <Control.text
              updateOn="blur"
              model=".CompanyNumber"
              type="number"
              placeholder={t("Registration Number")}
              className="mb-2 pt-2 pb-2"
            />
          </FormGroup>

          <Row>
            <Col>
              <FormGroup label={t("ID Type")} model=".id_type" className="pt-2 pb-2">
                <Control
                  component={Select}
                  model=".id_type"
                  labelKey="label"
                  valueKey="id"
                  placeholder={t("Select...")}
                  options={idTypes}
                  simpleValue
                  className="mb-2" style={{ backgroundColor: '#F4F7FE' }}
                />
              </FormGroup>
            </Col>
            {item.id_type === 'OTH' && <Col>
              <FormGroup label={t("ID Name")} model=".id_name" className="pt-2 pb-2">
                <Control.text
                  updateOn="blur"
                  model=".id_name"
                  placeholder={t("ID Name")}
                  className="mb-2 pt-2 pb-2"
                />
              </FormGroup>
            </Col>}
            <Col>
              <FormGroup label={t("ID Number")} model=".id_number" className="pt-2 pb-2">
                <Control.text
                  updateOn="blur"
                  model=".id_number"
                  placeholder={t("ID Number")}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      }
      <hr />
      <div className="d-flex justify-content-between">
        <Button color="danger" onClick={() => toggle()} className="my-3 mx-0"><Trans>Close</Trans></Button>
        <SaveButton className="my-3 mx-0" disabled={form?.['$form']?.pending}>
          {form?.['$form']?.pending ? <span><Trans>Saving</Trans></span> : <Trans>Save</Trans>}
        </SaveButton>
      </div>


    </LocalForm>
  )
};

export const enhance = compose(
  withReload(),
  withStateHandlers(
    {},
    {
      setState: () => (state) => ({ ...state }),
    }
  ),
  withPropsOnChange([], ({ item, setState, }) => {
    Promise.props({
      item: item,
      ready: true,
    }).then(setState);
  }),
  branch(
    (props) => !props.ready,
    () => () => null
  ),
  withXeroErrors(),
  withHandlers({
    onSubmit:
      ({
        xero,
        history,
        match,
        dispatch,
        setState,
        t,
        setXeroErrors,
        toggle,
        callbackFunc,
        missFields,
        invoice,
        setInvoice,
      }) =>
        (tmpItem) => {

          dispatch(actions.setTouched('', true));

          let item = tmpItem;

          const missingMobileNumber = missFields && (missFields.includes('PhoneNumber') || missFields.includes('PhoneCountryCode'))

          if (tmpItem.Addresses) {
            item = produce(item, (draft) => {
              const { Addresses } = draft;
              draft.Addresses = Addresses.map((address) => {
                if (address.Country === null) {
                  return { ...address, Country: "" };
                }
                return address;
              });
            });
          }

          const { SalesTrackingCategories: SalesTrackingCategories, PurchasesTrackingCategories: PurchasesTrackingCategories, ...item2 } = item;

          item = { ...item2 }

          let address = item?.Addresses?.find(address => address.AddressType === 'POBOX')

          let errors = [];

          if (missingMobileNumber) {
            let phone = item?.Phones?.find((phone) => phone['PhoneType'] === "MOBILE")

            if (!phone?.PhoneCountryCode || !phone?.PhoneNumber) {
              errors.push(t(`Phone Country Code and Phone Number is required.`));
            }

          } else {
            const requiredFields = [];

            errors = requiredFields
              .filter(({ field }) => !address[field])
              .map(({ message }) => message);

              if ( !item.TaxNumber && !(item.id_number && item.id_type) && !item.CompanyNumber ){      
              const errorMessage =
                item.id_type === 'OTH'
                  ? t(`ID Name and ID Number are required.`)
                  : t(`VAT or ID Type, ID Number is required.`);

              errors.push(errorMessage);
            }

            if (item.id_type === 'CRN' && item.id_number) {
              item.CompanyNumber = item.id_number
            }

            if (!item.CompanyNumber && !(item.id_type === 'CRN' && item.id_number)) {
              errors.push(t(`Registration Number is required.`));
            }
          }

          if (errors.length > 0) {
            setXeroErrors(errors);
            dispatch(actions.setPending("local", false));
            return;
          }

          setXeroErrors(null);

          const p = xero.put(`contacts/${item.ContactID}`, item)

          return p
            .get("data")
            .tap((response) => {
              toggle()
              NotificationManager.success(t("Saved successfully"));

              const taxAddress = response?.Addresses?.find(address => address.AddressType === 'POBOX');

              let contactInfo = {
                ContactVat: response.TaxNumber,
                ContactCity: taxAddress?.City,
                ContactStreet: taxAddress?.AddressLine1,
                ContactCountryCode: taxAddress?.Country,
                ContactBuilding: taxAddress?.AddressLine4,
                ContactPostalCode: taxAddress?.PostalCode,
                ContactState: taxAddress?.Region,
                ContactPlot: taxAddress?.AddressLine3,
                ContactIdType: item.id_type,
                ContactIdNumber: item.id_number,
                RegistrationNumber: item.CompanyNumber,
                CompanyNumber: item.CompanyNumber,
              }
              callbackFunc({ ...invoice, ...contactInfo })
              setInvoice({ ...invoice, Contact: response })
            })
            .catch((err) => {
              dispatch(actions.setPending("local", false));
              setXeroErrors(err);
            })
        },
  })
);

export default enhance(PartialContactForm);
